import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box, Typography, Snackbar, Button, withStyles } from '@material-ui/core';
import AttachFile from '@material-ui/icons/AttachFile';
import ReactS3Uploader from 'react-s3-uploader';
import { FormattedMessage, injectIntl } from 'react-intl';
import Input from 'components/FormInputs/Input';
import DialogTemplate from 'components/shared/DialogTemplate';
import CircularProgress from 'components/shared/CircularProgress';
import { DndProvider } from 'react-dnd';
import Html5Backend from 'react-dnd-html5-backend';
import TouchBackend from 'react-dnd-touch-backend';
import { isMobile } from 'react-device-detect';
import api from 'api';
import ImageDrag from './_drag_n_drop_image';
import EditDialog from './EditDialog';

const Backend = isMobile ? TouchBackend : Html5Backend;

/**
 * Loads an image and keeps the base64 so
 * the component refresh doesnt reload the remote image
 * over and over again
 */
const loadTmpImage = (image, cb) => {
  const img = new Image();

  img.onload = () => {
    const canvas = document.createElement('CANVAS');
    const ctx = canvas.getContext('2d');

    canvas.height = img.naturalHeight;
    canvas.width = img.naturalWidth;
    ctx.drawImage(img, 0, 0);
    const dataURL = canvas.toDataURL();

    return cb({
      ...image,
      base64: dataURL,
    });
  };

  img.onerror = () =>
    cb({
      ...image,
    });

  // img.src = `${image.url}?w=136&h=136&fit=crop`;
  img.src = `/img/${image.filename}?w=136&h=136&fit=crop`;
};

const styles = (theme) => ({
  images: {
    margin: -theme.spacing(1) / 2,
    marginTop: theme.spacing(2),
  },
  img: {
    width: 136,
    height: 136,
    backgroundColor: theme.palette.grey[500],
    backgroundSize: 'cover',
    display: 'inline-block',
    margin: theme.spacing(0.5),
    textAlign: 'left',
    position: 'relative',
    boxShadow: theme.shadows[4],
  },
  pointer: {
    cursor: 'move',
  },
  move: {
    cursor: 'move',
  },
  isOver: {
    opacity: 0.5,
  },
  imgSmall: {
    width: 100,
    height: 100,
  },
  imgFull: {
    maxWidth: '100%',
  },
  editButton: {
    margin: theme.spacing(1),
    position: 'absolute',
    right: 0,
    top: 0,
  },
  num: {
    margin: theme.spacing(1),
    color: theme.palette.common.white,
    textShadow: '2px 2px 2px rgba(0, 0, 0, 0.5)',
  },
});

class EditImages extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      edit: false,
      imageUploading: null,
      uploadFailure: null,
      rejectedFiles: [],
      tmpImages: props.cottage.images,
      base64Map: {},
      youtubeVideo: props.cottage.youtube_video || '',
      errors: {},
      prevCottageId: props.cottage.id,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.cottage.id !== prevState.prevCottageId) {
      return {
        tmpImages: nextProps.cottage.images || [],
        youtubeVideo: nextProps.cottage.youtube_video || '',
        errors: {},
        base64Map: {},
        prevCottageId: nextProps.cottage.id,
      };
    }

    return null;
  }

  componentDidMount() {
    this.setState(
      {
        tmpImages: this.props.cottage.images || [],
        youtubeVideo: this.props.cottage.youtube_video || '',
        errors: {},
      },
      () => {
        this.fetchBase64();
      },
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.tmpImages !== this.state.tmpImages) {
      this.fetchBase64();
    }
  }

  fetchBase64 = () => {
    this.state.tmpImages.forEach((image) => {
      if (!this.state.base64Map[image.filename]) {
        loadTmpImage(image, ({ base64 }) => {
          this.setState({
            base64Map: {
              ...this.state.base64Map,
              [image.filename]: base64,
            },
          });
        });
      }
    });
  };

  markFileRejected = (file) => {
    this.setState({
      rejectedFiles: this.state.rejectedFiles.concat([`"${file.name}"`]),
    });
  };

  closeFilesRejected = () => {
    this.setState({ rejectedFiles: [] });
  };

  onPreprocess = (file, next) => {
    if (!/\.(jpg|jpeg|png)$/i.test(file.name)) {
      this.setState({
        imageUploading: null,
        uploadFailure: true,
      });
      return;
    }

    const fileReader = new FileReader();
    const img = new Image();

    const markFileRejected = this.markFileRejected.bind(this);
    const onError = () => {
      this.setState({
        imageUploading: null,
        uploadFailure: true,
      });
    };

    fileReader.onload = () => {
      img.src = fileReader.result;
      img.onload = () => {
        if (img.width < 500 || img.height < 500) {
          markFileRejected(file);
          return;
        }

        if (file.size > 10 * 1024 * 1024) {
          markFileRejected(file);
          return;
        }

        // eslint-disable-next-line no-param-reassign
        file.width = img.width;
        // eslint-disable-next-line no-param-reassign
        file.height = img.height;

        next(file);
      };

      img.onerror = () => {
        onError();
      };
    };
    fileReader.readAsDataURL(file);
  };

  onUploadProgress = (_imageUploading) => {
    if (!this.state.imageUploading || _imageUploading > this.state.imageUploading) {
      this.setState({ imageUploading: _imageUploading });
    }
  };

  onUploadSuccess = (e, file) => {
    const image = {
      filename: e.name,
      url: e.url,
      width: file.width,
      height: file.height,
    };

    this.setState(
      {
        tmpImages: this.state.tmpImages.concat([image]),
        imageUploading: null,
      },
      () => {
        this.fetchBase64();
      },
    );
  };

  onUploadFailure = () => {
    this.setState({
      imageUploading: null,
      uploadFailure: true,
    });
  };

  editImg = (_edit) => {
    this.setState({ edit: _edit });
  };

  onDelete = () => {
    const images = this.state.tmpImages.filter((v, i) => i !== this.state.edit);

    this.setState({
      tmpImages: images,
      edit: false,
    });
  };

  onEditSubmit = (updatedValues) => {
    const { edit, tmpImages } = this.state;
    const image = tmpImages.find((v, i) => i === edit);

    if (!image) {
      return;
    }

    const images = [
      ...tmpImages.slice(0, edit),
      {
        ...image,
        ...updatedValues,
      },
      ...tmpImages.slice(edit + 1),
    ];

    this.setState({
      tmpImages: images,
      edit: false,
    });
  };

  handleGetSignedUrl = (file, callback) => {
    api.getCottageSignedUrl(this.props.cottage.id, file, callback);
  };

  closeError = () => {
    this.setState({
      uploadFailure: null,
    });
  };

  moveImage = () => {
    this.fetchBase64();
  };

  hoverImage = (drag, drop) => {
    const { tmpImages } = this.state;
    const remaining = tmpImages.filter((a, i) => i !== drag);
    const dragging = tmpImages[drag];

    const reordered = [...remaining.slice(0, drop), dragging, ...remaining.slice(drop)];

    this.setState({ tmpImages: reordered });
  };

  submitForm = () => {
    const youtubeError = this.validateYoutubeVideo(this.state.youtubeVideo);
    if (youtubeError) {
      this.setState({ errors: { youtube_video: youtubeError } });
      return;
    }

    this.props.onSubmit({
      images: this.state.tmpImages,
      youtube_video: this.state.youtubeVideo || null,
    });
  };

  validateYoutubeVideo = (value) => {
    if (!value) {
      return null;
    }

    const isValid = /^[a-zA-Z0-9_-]{6,50}$/.test(value);
    if (isValid) {
      return null;
    }

    return this.props.intl.formatMessage({
      id: 'cottages.youtubeVideo.invalid',
      defaultMessage: 'Enter a valid YouTube video id',
    });
  };

  render() {
    const { isLoading, onClose, cottage, classes } = this.props;
    const {
      edit,
      imageUploading,
      uploadFailure,
      rejectedFiles,
      tmpImages,
      base64Map,
      youtubeVideo,
      errors,
    } = this.state;

    return (
      <DialogTemplate
        title={
          <>
            <FormattedMessage id="Images" defaultMessage="Images" />
            &nbsp;
            <>
              #{cottage?.id}
              {cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}
            </>
          </>
        }
        onSave={this.submitForm}
        progress={imageUploading}
        pristine={
          JSON.stringify(cottage.images) === JSON.stringify(tmpImages) &&
          (cottage.youtube_video || '') === youtubeVideo
        }
        onClose={onClose}
        isUpdating={isLoading}
      >
        <Box boxShadow={3} px={2} py={3}>
          {edit === false && (
            <>
              <Typography variant="h6" component="h3" gutterBottom>
                <FormattedMessage id="Images" defaultMessage="Images" />
              </Typography>
              <Box textAlign="center">
                <Button color="primary" aria-label="Attach" component="label" htmlFor="uploadFile">
                  {imageUploading !== null ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <AttachFile />
                  )}
                  <FormattedMessage
                    id="Upload new image"
                    defaultMessage="Upload new image (JPEG or PNG format)"
                  />
                  <ReactS3Uploader
                    multiple
                    accept=".jpeg,.jpg,.png"
                    getSignedUrl={this.handleGetSignedUrl}
                    preprocess={this.onPreprocess}
                    onProgress={this.onUploadProgress}
                    onError={this.onUploadFailure}
                    onFinish={this.onUploadSuccess}
                    uploadRequestHeaders={{ 'x-amz-acl': 'private' }}
                    contentDisposition="auto"
                    key={Math.random()}
                    style={{ display: 'none' }}
                    id="uploadFile"
                  />
                </Button>
                <hr />
              </Box>
              <DndProvider backend={Backend}>
                {!!tmpImages && tmpImages.length > 1 && (
                  <Typography component="div" align="center">
                    <FormattedMessage
                      id="dragImageHelp"
                      defaultMessage="Drag and drop images to sort them in the order you like them to be"
                    />
                  </Typography>
                )}

                <div className={classNames(classes.images, classes.pointer)}>
                  {!!tmpImages &&
                    tmpImages.map((image, i) => (
                      <ImageDrag
                        src={
                          base64Map[image.filename]
                            ? base64Map[image.filename]
                            : `/img/${image.filename}?w=136&h=136&fit=crop`
                        }
                        id={i + 1}
                        index={i}
                        key={i}
                        classes={classes}
                        onEdit={() => this.editImg(i)}
                        onHoverImage={this.hoverImage}
                        onMoveImage={this.moveImage}
                      />
                    ))}
                </div>
              </DndProvider>
              <Box mt={6} mb={1}>
                <Typography variant="body2" component="span">
                  <FormattedMessage
                    id="cottages.youtubeVideo.optionalLabel"
                    defaultMessage="Add YouTube video (optional)"
                  />
                </Typography>
              </Box>
              <Box mt={1}>
                <Input
                  name="youtube_video"
                  label={
                    <FormattedMessage
                      id="cottages.youtubeVideo.label"
                      defaultMessage="YouTube video"
                    />
                  }
                  help={
                    <FormattedMessage
                      id="cottages.youtubeVideo.help"
                      defaultMessage="Video YouTube-ID (Example: DT6TFZ9Zbw9)"
                    />
                  }
                  value={youtubeVideo}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    this.setState({
                      youtubeVideo: nextValue,
                      errors: {
                        ...errors,
                        youtube_video: this.validateYoutubeVideo(nextValue),
                      },
                    });
                  }}
                  onBlur={() => {
                    this.setState({
                      errors: {
                        ...errors,
                        youtube_video: this.validateYoutubeVideo(youtubeVideo),
                      },
                    });
                  }}
                  errors={errors}
                  inputProps={{ maxLength: 50 }}
                />
              </Box>
            </>
          )}
          {edit !== false && (
            <EditDialog
              onSubmit={this.onEditSubmit}
              onClose={() => this.editImg(false)}
              onDelete={this.onDelete}
              images={tmpImages}
              edit={edit}
            />
          )}
        </Box>
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          message={
            <FormattedMessage
              id="cottages.failImageUpload_1"
              defaultMessage="Something wrong with uploading, please try again, make sure you select files in JPEG or PNG formats"
            />
          }
          open={uploadFailure}
          onClose={(e) => {
            if (e && e.type === 'click') {
              return;
            }

            return this.closeError();
          }}
          autoHideDuration={5000}
        />
        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          message={
            <FormattedMessage
              id="cottages.failImageUpload_2"
              defaultMessage="The following files are not valid: {filenames}. The image dimensions needs to be at least 500x500 pixels and the size of the file have to be less than 10MB."
              values={{
                filenames: rejectedFiles.join(', '),
              }}
            />
          }
          open={!!rejectedFiles.length}
          autoHideDuration={10000}
          onClose={(e) => {
            if (e && e.type === 'click') {
              return;
            }

            return this.closeFilesRejected();
          }}
        />
      </DialogTemplate>
    );
  }
}

EditImages.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({})),
    youtube_video: PropTypes.string,
  }),
  classes: PropTypes.shape({}),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(withStyles(styles)(EditImages));
